<template>
  <div class="my-1 py-25">
    <div v-if="isLoading" class="text-center">
      <b-spinner label="Loading..." />
    </div>
    <div v-else>
      <div v-for="section in sections" :key="section.type" class="mb-2">
        <h6 class="mb-1">{{ section.title }}</h6>

        <!-- Кадры: сетка превью -->
        <div v-if="section.type === 'image' && itemsByType('image').length" class="media-grid">
          <div v-for="item in itemsByType('image')" :key="item.id" class="media-item-wrapper">
            <a :href="item.url" target="_blank" rel="noopener">
              <b-img :src="item.url" class="media-img" loading="lazy" />
            </a>
            <div class="small text-truncate" :title="item.title">{{ item.title }}</div>
            <b-button
                variant="flat-danger"
                size="sm"
                class="btn-icon media-delete-btn"
                @click="deleteItem(item)"
            >
              <feather-icon icon="TrashIcon" />
            </b-button>
          </div>
        </div>

        <!-- Аудио: плеер + подпись -->
        <div v-else-if="section.type === 'audio' && itemsByType('audio').length">
          <div
              v-for="item in itemsByType('audio')"
              :key="item.id"
              class="d-flex align-items-center mb-50"
          >
            <audio :src="item.url" controls preload="none" class="media-audio mr-1" />
            <span class="text-truncate mr-auto" :title="item.title">{{ item.title }}</span>
            <b-button variant="flat-secondary" size="sm" class="btn-icon" @click="renameItem(item)">
              <feather-icon icon="Edit2Icon" />
            </b-button>
            <b-button variant="flat-danger" size="sm" class="btn-icon" @click="deleteItem(item)">
              <feather-icon icon="TrashIcon" class="text-danger" />
            </b-button>
          </div>
        </div>

        <!-- Видео: компактный плеер + подпись -->
        <div v-else-if="section.type === 'video' && itemsByType('video').length" class="media-videos">
          <div v-for="item in itemsByType('video')" :key="item.id" class="media-video-wrapper mb-1">
            <video :src="item.url" controls preload="metadata" class="media-video" />
            <div class="d-flex align-items-center">
              <span class="text-truncate mr-auto" :title="item.title">{{ item.title }}</span>
              <b-button variant="flat-secondary" size="sm" class="btn-icon" @click="renameItem(item)">
                <feather-icon icon="Edit2Icon" />
              </b-button>
              <b-button variant="flat-danger" size="sm" class="btn-icon" @click="deleteItem(item)">
                <feather-icon icon="TrashIcon" class="text-danger" />
              </b-button>
            </div>
          </div>
        </div>

        <div v-else class="text-muted small mb-50">Пока пусто</div>

        <!-- Загрузка своего файла -->
        <div class="d-flex align-items-center flex-wrap mt-50">
          <b-form-file
              v-model="uploadFiles[section.type]"
              :accept="acceptByType[section.type]"
              size="sm"
              class="media-file-input mr-50 mb-50"
              placeholder="Выбрать файл..."
              browse-text="Обзор"
          />
          <b-form-input
              v-model="uploadTitles[section.type]"
              size="sm"
              placeholder="Подпись"
              class="media-title-input mr-50 mb-50"
          />
          <b-button
              variant="primary"
              size="sm"
              class="mb-50"
              :disabled="!uploadFiles[section.type] || isUploading"
              @click="upload(section.type)"
          >
            <b-spinner v-if="isUploading" small class="mr-50" />
            <feather-icon v-else icon="UploadIcon" class="mr-50" />
            <span>Загрузить</span>
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BSpinner,
  BImg,
  BButton,
  BFormFile,
  BFormInput,
} from 'bootstrap-vue'
import store from '@/store'
import { ref, reactive, onMounted } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'MovieMediaTab',
  components: {
    BSpinner,
    BImg,
    BButton,
    BFormFile,
    BFormInput,
  },
  props: {
    movieId: { type: [Number, String], required: true },
    category: { type: String, required: true },
    sections: { type: Array, required: true }, // [{type: 'audio', title: 'Аудио'}, ...]
  },
  setup(props) {
    const toast = useToast()

    const items = ref([])
    const isLoading = ref(false)
    const isUploading = ref(false)
    const uploadFiles = reactive({ audio: null, image: null, video: null })
    const uploadTitles = reactive({ audio: '', image: '', video: '' })

    const acceptByType = {
      audio: '.mp3',
      image: '.jpg,.jpeg,.png,.webp',
      video: '.mp4,.webm,.mov',
    }

    const itemsByType = type => items.value.filter(item => item.type === type)

    const fetchItems = () => {
      isLoading.value = true
      store.dispatch('movies/fetchMovieMedia', { id: props.movieId, category: props.category })
          .then(response => {
            items.value = response.data.data
          })
          .catch(() => {
            toast({
              component: ToastificationContent,
              props: {
                title: 'Ошибка при загрузке медиа',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          })
          .finally(() => {
            isLoading.value = false
          })
    }

    const upload = type => {
      const formData = new FormData()
      formData.append('category', props.category)
      formData.append('type', type)
      formData.append('file', uploadFiles[type])
      if (uploadTitles[type]) formData.append('title', uploadTitles[type].slice(0, 255))

      isUploading.value = true
      store.dispatch('movies/uploadMovieMedia', { id: props.movieId, formData })
          .then(response => {
            items.value.push(response.data.data)
            uploadFiles[type] = null
            uploadTitles[type] = ''
            toast({
              component: ToastificationContent,
              props: {
                title: 'Файл загружен',
                icon: 'CheckIcon',
                variant: 'success',
              },
            })
          })
          .catch(() => {
            toast({
              component: ToastificationContent,
              props: {
                title: 'Ошибка при загрузке файла',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          })
          .finally(() => {
            isUploading.value = false
          })
    }

    const renameItem = item => {
      // Простое переименование без модалки — админский инструмент
      const title = window.prompt('Подпись', item.title || '')
      if (title === null) return
      store.dispatch('movies/updateMovieMedia', { movie_id: props.movieId, id: item.id, title })
          .then(response => {
            Object.assign(item, response.data.data)
          })
          .catch(() => {
            toast({
              component: ToastificationContent,
              props: {
                title: 'Ошибка при переименовании',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          })
    }

    const deleteItem = item => {
      if (!window.confirm('Удалить файл? Он будет удалён и с S3.')) return
      store.dispatch('movies/deleteMovieMedia', { movie_id: props.movieId, id: item.id })
          .then(() => {
            items.value = items.value.filter(existing => existing.id !== item.id)
          })
          .catch(() => {
            toast({
              component: ToastificationContent,
              props: {
                title: 'Ошибка при удалении файла',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          })
    }

    onMounted(fetchItems)

    return {
      items,
      isLoading,
      isUploading,
      uploadFiles,
      uploadTitles,
      acceptByType,
      itemsByType,
      upload,
      renameItem,
      deleteItem,
    }
  },
}
</script>

<style lang="scss" scoped>
.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.75rem;
}
.media-item-wrapper { position: relative; }
.media-img { width: 100%; border-radius: 0.357rem; object-fit: cover; }
.media-delete-btn { position: absolute; top: 2px; right: 2px; }
.media-audio { height: 32px; max-width: 320px; }
.media-video { max-width: 480px; width: 100%; border-radius: 0.357rem; }
.media-file-input { max-width: 260px; }
.media-title-input { max-width: 200px; }
</style>
